import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Autocomplete,
  Divider,
} from "@mui/material";
import ReusableDialog from "../../common/dialog/dialog";
import { useSelector } from "react-redux";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { format, isValid, parse } from "date-fns";

// DailyLogsTable Component
const DailyLogs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const user = useSelector((state) => state.auth.user); // User data from Redux store
  const [selectedTask, setSelectedTask] = useState(null); // State for selected task
  const [dialogOpen, setDialogOpen] = useState(false);
  const [recordType, setRecordType] = useState("");
  const [incidentTime, setIncidentTime] = useState(() => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // Extracts HH:MM format
  });
  const [description, setDescription] = useState("");
  const [tasks, setTasks] = useState([]);

  const fetchTasks = async () => {
    try {
      const response = await axios.get("/api/assessments/assetAll", {
        headers: {
          "x-user-role": user?.roleid, // Custom header
        },
      });
      const completedTasks = response.data.filter((task) => task.status === "completed");
      setTasks(completedTasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    }
  };

  useEffect(() => {
    fetchTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get("/api/dailyLog/logAll");
      setLogs(response.data); // Update logs state with API response
    } catch (error) {
      console.error("Error fetching daily logs:", error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, []);

  const handleSubmit = async () => {
    if (!incidentTime || !description || !recordType) {
      alert("Please fill in all fields");
      return;
    }

    const newLog = {
      time: incidentTime,
      description,
      category: recordType,
      reportedBy: user?.username || "Unknown User",
      status: recordType === "General Record" ? "completed" : "pending",
    };

    try {
      const response = await axios.post("/api/dailyLog/log", newLog);
      setLogs((prevLogs) => [response.data, ...prevLogs]); // Update logs state with API response
    } catch (error) {
      console.error("Error submitting daily log:", error);
      alert("Failed to submit daily log.");
    }

    setDialogOpen(false); // Close Dialog
    setRecordType("");
    setIncidentTime(new Date().toTimeString().slice(0, 5)); // Reset time
    setDescription("");
  };

  return (
    <Box sx={{ pl: 4, pr: 4, ml: 2 }}>
      <Box>
        <Typography sx={{ fontSize: "24px", fontWeight: "bold", color: "black" }}>Daily Logs</Typography>
      </Box>
      <Grid container spacing={2} mt={2}>
        <Grid item lg={9} md={9}>
          <Typography variant='body1' color='gray'>
            Records all daily activities, incidents, Course for concern etc.
          </Typography>

          <Button variant='outlined' sx={{ mt: 2, mb: 2 }} onClick={() => setDialogOpen(true)}>
            + Add new record
          </Button>

          <Box sx={{ mt: 2, mb: 2 }} className='scrollable-task-list'>
            {/* Table Header */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                backgroundColor: "#E3F2FD",
                padding: "8px 16px",
                borderRadius: "8px",
              }}
            >
              <Grid container alignItems='center' spacing={2}>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Time</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Description</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Category</Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Reported by</Typography>
                </Grid>
                <Grid item xs={2}>
                  <Typography sx={{ fontSize: "14px", color: "blue" }}>Reporting Status</Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Table Content */}
            {logs?.length > 0 || tasks?.length > 0 ? (
              [...logs, ...tasks].map((log, index) => (
                <Card
                  key={index}
                  sx={{
                    mt: 1,
                    boxShadow: 1,
                  }}
                  onClick={() => {
                    setSelectedTask(log);
                  }}
                >
                  <CardContent>
                    <Grid container alignItems='center' spacing={2}>
                      <Grid item xs={2}>
                        <Typography sx={{ fontSize: "14px", color: "black" }}>
                          {log.time && isValid(parse(log.time, "HH:mm", new Date()))
                            ? format(parse(log.time, "HH:mm", new Date()), "hh:mm a")
                            : isValid(new Date(log.updated_at))
                            ? format(new Date(log.updated_at), "hh:mm a")
                            : "Invalid time"}
                        </Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography sx={{ fontSize: "14px", color: "black" }}>{log.description}</Typography>
                      </Grid>
                      <Grid item xs={2}>
                        <Chip
                          label={log.category || "Tasks"}
                          sx={{ backgroundColor: log.categoryColor, color: "#333" }}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <Box display='flex' alignItems='center'>
                          <Avatar
                            alt='Profile'
                            src='/images/person.png'
                            className='profile-avatar'
                            sx={{ width: 40, height: 40, marginRight: 2 }}
                          />
                          {log.reportedBy || log.updated_by}
                        </Box>
                      </Grid>
                      <Grid item xs={2}>
                        <Chip
                          label={log.status}
                          sx={{
                            backgroundColor: log.status === "completed" ? "#EAFFF2" : "#FFDDCC",
                            color: log.status === "completed" ? "green" : "red",
                          }}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Box display='flex' justifyContent='center' alignItems='center'>
                <img src='/images/file.gif' alt='Loading...' width='200' height='200' />
              </Box>
            )}
          </Box>
        </Grid>

        <Grid item lg={3} md={3}>
          {selectedTask ? (
            <Box sx={{ p: 2, ml: 2 }}>
              <Typography sx={{ fontSize: "20px", color: "black", fontWeight: "bold", mb: 2 }}>More Info</Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography sx={{ fontSize: "14px", color: "black", fontWeight: "bold", mb: 2 }}>
                {selectedTask.category || "Tasks"}
              </Typography>

              <Typography sx={{ fontSize: "14px", mb: 3 }}>{selectedTask.description}</Typography>

              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>Category</Typography>
                </Grid>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>{selectedTask.category || "Tasks"}</Typography>
                </Grid>
              </Grid>

              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>Status</Typography>
                </Grid>
                <Grid item lg={6} md={6}>
                  <Chip
                    variant='contained'
                    sx={{
                      fontSize: "12px",
                      borderRadius: 5,
                      backgroundColor: selectedTask.status === "completed" ? "#EAFFF2" : "#FFDDCC",
                      color: selectedTask.status === "completed" ? "green" : "#FF5733",
                    }}
                    label={selectedTask.status}
                    size='small'
                  />
                </Grid>
              </Grid>

              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>Assigned by</Typography>
                </Grid>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {selectedTask.reportedBy || selectedTask.updated_by}
                  </Typography>
                </Grid>
              </Grid>
              <Grid container spacing={1} alignItems='center' sx={{ mb: 2 }}>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>Time</Typography>
                </Grid>
                <Grid item lg={6} md={6}>
                  <Typography sx={{ fontSize: "14px" }}>
                    {selectedTask.time || format(new Date(selectedTask.updated_at), "hh.mm a ")}
                  </Typography>
                </Grid>
              </Grid>

              {selectedTask.category !== "General Record" &&
                selectedTask.status !== "completed" &&
                (selectedTask.reportedBy === user.username || selectedTask.updated_by === user.username) && (
                  <Button
                    variant='contained'
                    fullWidth
                    sx={{ mt: 3 }}
                    onClick={() =>
                      selectedTask.category === "Incident Management"
                        ? navigate(`/logs-incident?id=${selectedTask.id}`)
                        : selectedTask.category === "Cause For Concern"
                        ? navigate(`/cause-concern?id=${selectedTask.id}`)
                        : navigate(`/non-physical?id=${selectedTask.id}`)
                    }
                  >
                    {(selectedTask.category === "Incident Management" &&
                      localStorage.getItem(`dailyLogFormData-${selectedTask.id}`)) ||
                    (selectedTask.category === "Cause For Concern" &&
                      localStorage.getItem(`causeConcernFormData-${selectedTask.id}`)) ||
                    (selectedTask.category === "Safeguarding" &&
                      localStorage.getItem(`nonPhysicalFormData-${selectedTask.id}`))
                      ? "Resume Report"
                      : "Start Main Report"}
                  </Button>
                )}
            </Box>
          ) : (
            <Box sx={{ p: 2, ml: 2 }}>
              <Typography sx={{ fontSize: "20px", color: "black", fontWeight: "bold", mb: 2 }}>More Info</Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography sx={{ fontSize: "14px", mb: 3 }}>Add records to see more details</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <ReusableDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
        submitText='Submit'
        cancelText='Cancel'
      >
        <Chip
          variant='contained'
          sx={{
            fontSize: "14px",
            backgroundColor: "#F2F6FF",
            color: "blue",
          }}
          label='Daily Log'
          size='small'
        />
        <Typography sx={{ fontSize: "24px", color: "black", fontWeight: "bold" }}>Add new record</Typography>
        <Typography variant='subtitle2' sx={{ color: "#757575" }}>
          Date: {new Date().toLocaleDateString()} &nbsp; • &nbsp;{" "}
          <span style={{ color: "#1751D0" }}>{user.username}</span>
        </Typography>
        <Box sx={{ borderBottom: "1px solid #E0E0E0", mt: 2, mb: 2 }} />

        {/* Record Type Field */}

        <Autocomplete
          id='recordType'
          options={["Incident Management", "General Record", "Safeguarding", "Cause For Concern"]} // Get unique task names from tasks
          value={recordType}
          onChange={(event, newValue) => setRecordType(newValue)}
          renderInput={(params) => (
            <Grid container spacing={2}>
              {/* Incident Time */}
              <Grid item xs={2}>
                <Typography sx={{ fontSize: "14px", mt: 2 }} align='left'>
                  Record Type
                </Typography>
              </Grid>
              <Grid item xs={10}>
                <TextField {...params} placeholder='Search here .....' />
              </Grid>
            </Grid>
          )}
          fullWidth
          sx={{ mb: 2 }}
        />

        {/* Incident Management Section */}
        <Typography sx={{ fontSize: "14px", fontWeight: "bold", mb: 2 }} align='left'>
          Record Details
        </Typography>

        <Grid container spacing={2} sx={{ mb: 1 }}>
          {/* Incident Time */}
          <Grid item xs={6}>
            <Typography sx={{ fontSize: "14px" }} align='left'>
              Incident Time
            </Typography>
            <TextField
              fullWidth
              type='time'
              value={incidentTime}
              onChange={(e) => setIncidentTime(e.target.value)}
              sx={{ mb: 1 }}
            />
          </Grid>
        </Grid>

        {/* Description Field */}
        <Typography sx={{ fontSize: "14px" }} align='left'>
          Description
        </Typography>
        <TextField
          fullWidth
          variant='outlined'
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mt: 1 }}
        />
      </ReusableDialog>
    </Box>
  );
};

export default DailyLogs;
